import type { FormlyFieldConfig } from '@ngx-formly/core';
import type { Entry } from '../../features/admin/modules/modules.model';

export type MappingCondition = {
  compareField: string;
  compareSubField?: string;
  operator: string;
  value: unknown;
};

/**
 * Extract numeric array-indices from a Formly field's position so a condition
 * keyPath crossing a fieldArray gets resolved against the correct array item.
 *
 * Tries `field.path` first (dot-notation with numeric segments). Falls back to
 * walking the parent chain and collecting numeric `field.key` values, which is
 * how Formly tracks fieldArray-item position when path is not populated.
 */
export function extractArrayIndicesFromField(field: FormlyFieldConfig | undefined | null): number[] {
  if (!field) return [];

  const path = (field as unknown as { path?: string }).path;
  if (typeof path === 'string' && path.length > 0) {
    const fromPath = path
      .split('.')
      .filter(seg => /^\d+$/.test(seg))
      .map(Number);
    if (fromPath.length > 0) return fromPath;
  }

  const fromChain: number[] = [];
  let current: FormlyFieldConfig | undefined = field;
  while (current) {
    const k = current.key as unknown;
    if (typeof k === 'number') {
      fromChain.unshift(k);
    } else if (typeof k === 'string' && /^\d+$/.test(k)) {
      fromChain.unshift(Number(k));
    }
    current = current.parent;
  }
  return fromChain;
}

/**
 * Resolve a dot-notation keyPath against an entry, applying `arrayIndices` when
 * the path crosses an array segment. Fallback when no index is available for an
 * encountered array: collect the next-segment value from every item.
 */
export function resolveScopedKeyPath(root: unknown, keyPath: string, arrayIndices: number[]): unknown {
  if (root == null || !keyPath) return undefined;
  const parts = keyPath.split('.');
  let current: unknown = root;
  let cursor = 0;

  for (const part of parts) {
    if (current == null) return undefined;

    if (Array.isArray(current)) {
      if (cursor < arrayIndices.length) {
        current = (current as unknown[])[arrayIndices[cursor]];
        cursor++;
      } else {
        return (current as unknown[])
          .map(item => (item == null ? undefined : (item as Record<string, unknown>)[part]))
          .filter(v => v !== undefined);
      }
    }

    if (current == null) return undefined;
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Write `value` at `keyPath`, applying `arrayIndices` when the path crosses an array segment.
 * The write-side counterpart of `resolveScopedKeyPath`.
 *
 * Returns false — and writes nothing — when an array is crossed without an index left over.
 * `UtilityService.setKeyValuePath` falls back to writing *every* item in that case, which for an
 * auto-fill result means one row's lookup overwriting all sibling rows. Refusing the write leaves
 * the value to be filled by hand, which is the lesser wrong.
 */
export function setScopedKeyPath(
  root: unknown,
  keyPath: string,
  value: unknown,
  arrayIndices: number[] = []
): boolean {
  if (root == null || !keyPath) return false;

  const parts = keyPath.split('.');
  let current: unknown = root;
  let cursor = 0;

  for (let i = 0; i < parts.length; i++) {
    if (Array.isArray(current)) {
      if (cursor >= arrayIndices.length) return false;
      current = (current as unknown[])[arrayIndices[cursor]];
      cursor++;
    }
    if (current == null || typeof current !== 'object') return false;

    const container = current as Record<string, unknown>;
    const part = parts[i];

    if (i === parts.length - 1) {
      container[part] = value;
      return true;
    }

    if (container[part] == null) {
      container[part] = {};
    }
    current = container[part];
  }

  return false;
}

/**
 * Evaluates a list of mapping conditions against an entry. All conditions must pass (AND).
 *
 * - Empty/undefined `conditions` → returns true.
 * - Null/undefined `entry` → returns true (don't filter when context is missing).
 * - Array-valued `condition.value` → any-match (OR within the condition).
 * - Array-valued compareFieldValue → some-match.
 *
 * `checkCondition` is injected so callers can reuse `FormService.checkCondition`
 * without pulling Angular dependencies into this pure util. `fieldArrayIndices`
 * scopes keyPath resolution to a specific fieldArray-item position.
 */
export function evaluateMappingConditions(
  conditions: MappingCondition[] | undefined,
  entry: Entry | null | undefined,
  checkCondition: (condition: MappingCondition, value: unknown) => boolean,
  fieldArrayIndices: number[] = []
): boolean {
  if (!conditions?.length) return true;
  if (!entry) return true;

  return conditions.every(condition => {
    if (!condition) return true;

    const keyPath =
      condition.compareSubField != null && condition.compareSubField !== ''
        ? `${condition.compareField}.${condition.compareSubField}`
        : condition.compareField;
    const compareFieldValue = resolveScopedKeyPath(entry, keyPath, fieldArrayIndices);

    if (Array.isArray(condition.value)) {
      return condition.value.some(v => {
        const sub = { ...condition, value: v };
        if (Array.isArray(compareFieldValue)) {
          return compareFieldValue.some(cv => checkCondition(sub, cv));
        }
        return checkCondition(sub, compareFieldValue);
      });
    }

    return checkCondition(condition, compareFieldValue);
  });
}
